import { useContext } from "react";
import {
  EntitlementContext,
  ModuleEntitlementResult,
} from "../context/EntitlementContext";

/**
 * Hook for checking whether the current tenant has access to a given
 * module. Returns { granted, reason?, gating?, requiredTier?,
 * requiredAddon?, module? }. Unknown module keys are denied with
 * gating "hard" (matches backend's requireModule middleware; a typo must
 * not show a surface the server will refuse).
 *
 * Usage:
 *   const { granted, gating, requiredTier } = useModuleEntitled("local_seo");
 *   if (!granted && gating === "hard") return null;
 *   if (!granted) return <UpgradePrompt requiredTier={requiredTier} />;
 *
 * NOTE: This is purely a UX optimization. The backend's requireModule
 * middleware is the actual security boundary — never trust this hook
 * to enforce access. Use it to hide nav items / show upgrade overlays /
 * route-guard pages so users don't bump into 402s from the backend.
 */
export function useModuleEntitled(moduleKey: string): ModuleEntitlementResult {
  const { isModuleEntitled } = useContext(EntitlementContext);
  return isModuleEntitled(moduleKey);
}

/**
 * Hook returning the full entitlement context (tier, status, addons,
 * modules array, loading flag, refresh fn). Use the simpler
 * useModuleEntitled hook unless you need the broader state (e.g., to
 * delay a render until entitlements are loaded, or to render a tier
 * comparison).
 */
export function useEntitlements() {
  return useContext(EntitlementContext);
}
